import React, { useEffect, useState } from 'react';

import { AutoComplete, Collapse, Form, Input } from 'antd';
import type { InputStatus } from 'antd/es/_util/statusUtils';
import { useTranslation } from 'react-i18next';

import { NSKeys } from '@app/i18n';

import { DaDataAddress, DaDataSuggestion } from '@app/models/daData';

import type { IFieldProps, IParsedAddressValues } from '../../interfaces'; // оставь как у тебя

import { useDaDataAddressAutocomplete } from './hooks';

import { mapDaDataSuggestionToParsed } from './services';

import { Container } from './styles';

const TAB_ID = '1';

export const DaDataAutocomplete: React.FC<IFieldProps> = ({
  disabled = false,
  defaultOpen = true,
  label,
  name,
  wrapperCol = { span: 24 },
  required = false,
  ...props
}) => {
  const autocompleteT = useTranslation(NSKeys.autocomplete);
  const validationT = useTranslation(NSKeys.validation);

  const [activeKey, setActiveKey] = useState<string[]>(defaultOpen ? [TAB_ID] : []);
  const [status, setStatus] = useState<InputStatus>('');

  const [autoCompleteInput, setAutoCompleteInput] = useState('');

  const [country, setCountry] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [region, setRegion] = useState('');
  const [city, setCity] = useState('');

  const [hasSelected, setHasSelected] = useState(false);

  const { setQuery, suggestions, isSuggestLoading } = useDaDataAddressAutocomplete({
    debounceMs: 350,
    minChars: 4,
    count: 10,
  });

  const getName = (value: string) => (name ? (Array.isArray(name) && name.length ? [...name, value] : value) : value);

  useEffect(() => {
    const provider_address_uid = props.form.getFieldValue(getName('provider_address_uid'));
    if (provider_address_uid) {
      handleInitValues();
      setHasSelected(true);
    }

    if (!defaultOpen) setActiveKey([]);
  }, []);

  const handleInitValues = () => {
    console.log(props.form.getFieldValue(''));
    const street_name = props.form.getFieldValue(getName('street_name'));
    const street_number = props.form.getFieldValue(getName('street_number'));
    const postal_code = props.form.getFieldValue(getName('postal_code'));
    const country = props.form.getFieldValue(getName('country'));
    const city = props.form.getFieldValue(getName('city'));
    const region = props.form.getFieldValue(getName('region'));
    const place_name = props.form.getFieldValue(getName('place_name'));

    setCountry(country);
    setPostalCode(postal_code);
    setRegion(region);
    setCity(city);
    setAutoCompleteValue({ country, city, postal_code, region, street_name, street_number, place_name });
  };

  const setAutoCompleteValue = (data: IParsedAddressValues) => {
    const { city, country, postal_code, region, street_number, street_name, place_name } = data;

    setAutoCompleteInput(
      [country, postal_code, region, city, street_name, street_number, place_name].filter((el) => !!el).join(', ')
    );
  };

  const resetAddressFields = () => {
    props.form.setFieldValue(getName('provider_address_uid'), '');
    props.form.setFieldValue(getName('street_name'), '');
    props.form.setFieldValue(getName('street_number'), '');
    props.form.setFieldValue(getName('place_name'), '');
    props.form.setFieldValue(getName('provider'), '');

    setCity('');
    setRegion('');
    setPostalCode('');
    setCountry('');

    setHasSelected(false);
  };

  const handleChange = (newValue: string) => {
    setAutoCompleteInput(newValue);
    setQuery(newValue);
    resetAddressFields();
  };

  const handleSuggestionSelected = async (option: { label: string; value: string; data: DaDataAddress }) => {
    const parsed = mapDaDataSuggestionToParsed(option.data, option.label);

    setCountry(parsed.country);
    setPostalCode(parsed.postal_code);
    setRegion(parsed.region);
    setCity(parsed.city);

    setAutoCompleteInput(parsed.display);
    setQuery(parsed.display);

    props.form.setFieldValue(getName('provider_address_uid'), parsed.provider_address_uid);
    props.form.setFieldValue(getName('street_name'), parsed.street_name);
    props.form.setFieldValue(getName('street_number'), parsed.street_number);
    props.form.setFieldValue(getName('place_name'), parsed.place_name);
    props.form.setFieldValue(getName('provider'), 'dadata');

    setHasSelected(true);
    setStatus('');
  };

  const handleFieldsChange = () => {
    // если пользователь ручками поправляет street/house/place_name — пересобираем строку
    const street_name = props.form.getFieldValue(getName('street_name'));
    const street_number = props.form.getFieldValue(getName('street_number'));
    const place_name = props.form.getFieldValue(getName('place_name'));

    const display = [country, postalCode, region, city, street_name, street_number, place_name]
      .filter(Boolean)
      .join(', ');

    setAutoCompleteInput(display);
    setQuery(display);
  };

  return (
    <Container data-testid="dadata">
      <Collapse
        ghost
        collapsible="icon"
        destroyInactivePanel={false}
        expandIconPosition="end"
        activeKey={activeKey}
        size="small"
        onChange={(key) => setActiveKey(key as string[])}
        items={[
          {
            key: TAB_ID,
            label: (
              <Form.Item
                label={label || autocompleteT.t('label')}
                wrapperCol={wrapperCol}
                required={required}
                labelCol={{ span: 8 }}
                style={{ marginLeft: 12 }}>
                <AutoComplete
                  disabled={disabled}
                  onSearch={handleChange}
                  placeholder={autocompleteT.t('placeholder')}
                  options={suggestions?.suggestions?.map((suggestion: any) => ({
                    label: suggestion.value,
                    value: suggestion.unrestricted_value,
                    data: suggestion?.data,
                  }))}
                  notFoundContent={isSuggestLoading ? '...' : null}
                  onSelect={(e, option) => handleSuggestionSelected(option as any)}
                  onBlur={() => setStatus(hasSelected ? '' : required ? 'error' : '')}
                  onFocus={() => setStatus('')}
                  status={status}
                  value={autoCompleteInput}
                />
              </Form.Item>
            ),
            children: (
              <div>
                {/* скрытые поля */}
                <div style={{ display: 'none' }}>
                  <Form.Item
                    name={getName('provider_address_uid')}
                    rules={required ? [{ required: true, message: validationT.t('required') }] : undefined}>
                    <Input />
                  </Form.Item>
                  <Form.Item name={getName('provider')}>
                    <Input />
                  </Form.Item>
                </div>

                <Form.Item label={autocompleteT.t('country')}>
                  <Input disabled value={country} />
                </Form.Item>
                <Form.Item label={autocompleteT.t('postal_code')}>
                  <Input disabled value={postalCode} />
                </Form.Item>
                <Form.Item label={autocompleteT.t('region')}>
                  <Input disabled value={region} />
                </Form.Item>
                <Form.Item label={autocompleteT.t('city')}>
                  <Input disabled value={city} />
                </Form.Item>

                <Form.Item name={getName('street_name')} label={autocompleteT.t('street')} rules={props.rules}>
                  <Input disabled={disabled} maxLength={300} onChange={handleFieldsChange} />
                </Form.Item>
                <Form.Item name={getName('street_number')} label={autocompleteT.t('house')} rules={props.rules}>
                  <Input disabled={disabled} maxLength={150} onChange={handleFieldsChange} />
                </Form.Item>
                <Form.Item name={getName('place_name')} label={autocompleteT.t('office')}>
                  <Input disabled={disabled} maxLength={150} onChange={handleFieldsChange} />
                </Form.Item>
              </div>
            ),
          },
        ]}
      />
    </Container>
  );
};
